import { CODIGOS_QUE_PEDEM_LINK } from './linkDoPortal';

/**
 * A SINCRONIZAÇÃO DA PROPOSTA PELO JOB (16/10/2026, backend #465). O POST síncrono `scrape-from-portal` segurava a conexão
 * até o scrape acabar, e o nginx corta em 60 s: o TCE-PR (proposta 391, 15/10) viu "Erro: Network Error" enquanto o job
 * seguia rodando no servidor. Agora o POST `.../async` devolve o `jobId` na hora (202) e a tela consulta o job.
 *
 * O contrato (combinado com o Bastião):
 *  - 400/404 do POST são SÍNCRONOS (portal sem sincronização, proposta que não existe): sobem como erro, na hora;
 *  - `reaproveitado: true` não é erro: já havia um rodando, e este acompanha o mesmo;
 *  - `done` = status !== 'running'; consultar enquanto for false;
 *  - `completed` → `resultado` é o MESMO corpo do síncrono ({ ok, scrapedItems, hasMeepp, durationMs, documentos });
 *  - `failed` cobre exceção e ok:false; `erro` é a frase; `codigo` (ou null) é o da exceção. Código de link
 *    (LINK_NAO_RESOLVIDO…) sobe como a recusa que o `comLinkDoPortal` já sabe tratar ("cole o link do processo");
 *  - `desconhecido` volta 200 e é TERMINAL: o job saiu da memória do servidor; `erro` já traz a frase.
 *
 * Devolve o mesmo formato que a tela já lia do síncrono, para os chamadores não mudarem: { ok, scrapedItems, … } ou
 * { ok: false, error }.
 */
export type JobDeScrape = {
  jobId: string;
  status: 'running' | 'completed' | 'failed' | 'desconhecido' | string;
  done: boolean;
  erro?: string | null;
  codigo?: string | null;
  resultado?: any;
  progresso?: any;
  elapsedMs?: number;
};
export type InicioDoScrape = { jobId: string; status: string; reaproveitado?: boolean };

export const INTERVALO_DO_JOB_MS = 3000;
/** Consultas seguidas que podem falhar (rede) antes de a tela desistir de acompanhar. O job segue no servidor. */
export const FALHAS_DE_CONSULTA_TOLERADAS = 5;
/** O scrape do ComprasNet pode chegar a ~4 min com login frio; acima disto a tela para de esperar, o job não. */
export const LIMITE_DE_ESPERA_MS = 10 * 60 * 1000;

export const FRASE_SEM_CONTATO =
  'Perdi o contato com o servidor enquanto a sincronização rodava. Ela segue lá e pode ter terminado: recarregue a proposta para ver.';
export const FRASE_SEM_FIM =
  'A sincronização não terminou em 10 minutos. Ela segue no servidor: recarregue a proposta daqui a pouco.';
export const FRASE_REAPROVEITADO = 'Já havia uma sincronização desta proposta rodando: acompanhando a mesma.';

/** A recusa de link no formato que `recusaDeLink` lê do axios (`response.data.codigo` / `.mensagem`). */
function recusaDeLinkDoJob(job: JobDeScrape): Error | null {
  if (!job.codigo || !(CODIGOS_QUE_PEDEM_LINK as readonly string[]).includes(job.codigo)) return null;
  return Object.assign(new Error(job.erro || job.codigo), { response: { status: 400, data: { codigo: job.codigo, mensagem: job.erro || '' } } });
}

export async function sincronizarPeloJob(opts: {
  iniciar: () => Promise<InicioDoScrape>;
  consultar: (jobId: string) => Promise<JobDeScrape>;
  aoReaproveitar?: () => void;
  aoConsultar?: (job: JobDeScrape) => void;
  esperar?: (ms: number) => Promise<void>;
  agora?: () => number;
}): Promise<any> {
  const esperar = opts.esperar || ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const agora = opts.agora || (() => Date.now());
  const inicio = await opts.iniciar(); // 400/404 sobem daqui, na hora
  if (inicio.reaproveitado && opts.aoReaproveitar) opts.aoReaproveitar();
  const ate = agora() + LIMITE_DE_ESPERA_MS;
  let falhasSeguidas = 0;
  for (;;) {
    await esperar(INTERVALO_DO_JOB_MS);
    let job: JobDeScrape;
    try {
      job = await opts.consultar(inicio.jobId);
      falhasSeguidas = 0;
    } catch {
      falhasSeguidas += 1;
      if (falhasSeguidas >= FALHAS_DE_CONSULTA_TOLERADAS) return { ok: false, error: FRASE_SEM_CONTATO };
      if (agora() > ate) return { ok: false, error: FRASE_SEM_FIM };
      continue;
    }
    if (opts.aoConsultar) opts.aoConsultar(job);
    if (job.done || job.status !== 'running') {
      if (job.status === 'completed') return job.resultado ?? { ok: false, error: 'o servidor terminou sem devolver o resultado' };
      const link = recusaDeLinkDoJob(job);
      if (link) throw link;
      return { ok: false, error: job.erro || (job.status === 'desconhecido' ? 'o job não está mais no servidor' : 'a sincronização falhou') };
    }
    if (agora() > ate) return { ok: false, error: FRASE_SEM_FIM };
  }
}
